import { FC, memo } from 'react';

import { RoutePath } from '@common/constants';
import { useRoute, Redirect } from '@core/navigation';
import { useTranslation } from '@wearehelpful/ui-localization';
import { useGetCountiesInfinite } from '@helpful/network';
import {
  Content,
  Typography,
  Form,
  Skeleton,
  InfiniteScroll,
  Footer,
} from '@wearehelpful/ui-kit';

import { PayorForm, RouteSearch } from './County.types';
import { TAKE } from './County.const';

export const County: FC = memo(() => {
  const [
    {
      search: { zip },
    },
    { push },
  ] = useRoute<{}, RouteSearch>();
  const { t } = useTranslation();

  const { isLoading, data, hasNextPage, fetchNextPage } =
    useGetCountiesInfinite(
      {
        skip: 0,
        take: TAKE,
        filter: {
          zip: `${zip}`,
        },
      },
      {
        query: {
          getNextPageParam: ({ meta: { total } }, all) => {
            const loaded = all.flatMap(({ data }) => data).length;
            if (total > loaded) return TAKE * all.length;
          },
        },
      }
    );

  const payors = data?.pages.flatMap(({ data }) => data) || [];

  const handleOnSubmit = ({ fips }: PayorForm) => {
    push({
      path: RoutePath.INSURANCE_CONNECT_INSURANCE,
      search: { zip, fips },
    });
  };

  if (!zip) {
    return <Redirect path={RoutePath.AUTH_SIGN_UP} />;
  }

  return (
    <Form<PayorForm> onSubmit={handleOnSubmit}>
      <Content>
        <Content.Subheader>
          <Typography variant="h2">
            {t('subheadings.signUpCounty.title')}
          </Typography>
        </Content.Subheader>
        <Content.Fixed>
          <Skeleton variant="list" loading={isLoading}>
            <InfiniteScroll disabled={!hasNextPage} onScroll={fetchNextPage}>
              <Form.Section description={t('forms.countyOfYourLovedOne')}>
                <Form.RadioGroup name="fips" validation={{ required: true }}>
                  {payors.map(({ fips, name }) => {
                    return (
                      <Form.RadioGroup.Item key={fips} value={fips}>
                        {name}
                      </Form.RadioGroup.Item>
                    );
                  })}
                </Form.RadioGroup>
              </Form.Section>
            </InfiniteScroll>
          </Skeleton>
        </Content.Fixed>
      </Content>
      <Footer>
        <Form.Submit checkValidity disabled={isLoading}>
          {t('actions.next')}
        </Form.Submit>
      </Footer>
    </Form>
  );
});
